import React from 'react';
import { StandardTableActions, ColoredBadge, Badge, Select } from '../../components/ui';

import { Column } from '../../components/DataTable/DataTable';
import { SupportAgreement, SupportAgreementType } from './useData';
import { FieldConfig } from '../../components/generic/fieldRenderers';
import { FormLayout } from '../../components/generic/FormLayoutRenderer';
import { formatDate as formatDateGlobal, APP_CONFIG } from '../../config/appConfig';
import { cn } from '../../lib/utils';

import { renderAgreementRenewalStatusBadge } from './SupportAgreementRenewalRemindersSection';
import { SupportAgreementRenewalRemindersSection } from './SupportAgreementRenewalRemindersSection';

const SUPPORT_AGREEMENT_STATUS_COLOR_ACTIVE = '#22c55e';
const SUPPORT_AGREEMENT_STATUS_COLOR_INACTIVE = '#ef4444';

const renderSupportAgreementStatusBadge = (isActive?: boolean, className?: string) => (
  <ColoredBadge
    label={isActive ? 'Active' : 'Inactive'}
    color={isActive ? SUPPORT_AGREEMENT_STATUS_COLOR_ACTIVE : SUPPORT_AGREEMENT_STATUS_COLOR_INACTIVE}
    className={className}
  />
);

const renderAgreementServiceTypeBadge = (label?: string, color?: string) => (
  <ColoredBadge label={label} color={color} />
);

// Module Metadata
export const moduleConfig = {
  // Module Information
  module: {
    title: 'Support Agreements',
    description: 'Manage support agreements database',
    permissionModuleKey: 'support_agreements',
    route: {
      list: '/support-agreements',
      new: '/support-agreements/new',
      detail: (id: number) => `/support-agreements/${id}`,
      edit: (id: number) => `/support-agreements/${id}/edit`,
    },
    // API Endpoints
    api: {
      list: '/crm-support-agreements',
      detail: (id: number) => `/crm-support-agreements/${id}`,
    },
  },

  // Pagination Settings
  pagination: {
    initialItemsPerPage: APP_CONFIG.table.defaultPageSize,
    itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions,
  },

  // Search Settings
  search: {
    placeholder: 'Search support agreements by description, client, notes...',
  },

  // Filter Groups Configuration
  filterGroups: [
    {
      name: 'basic',
      label: 'Basic',
      icon: '🔍',
      fields: ['clientId', 'agreementType', 'isActive', 'startDate', 'endDate'],
    },
  ],

  // Filter Configuration
  filters: {
    fields: [
      {
        key: 'description',
        label: 'Description',
        placeholder: 'Type to filter description...',
        type: 'text' as const,
        getValue: (item: SupportAgreement) => item.description,
        options: [],
      },
      {
        key: 'clientId',
        label: 'Client',
        placeholder: 'All Clients',
        type: 'select' as const,
        getValue: (item: SupportAgreement) => item.client?.clientName || '',
        options: [],
        apiEndpoint: '/crm-clients',
        labelField: 'clientName',
      },
      {
        key: 'agreementType',
        label: 'Agreement Type',
        placeholder: 'All Types',
        type: 'select' as const,
        getValue: (item: SupportAgreement) => item.agreementType || '',
        options: [
          { value: 'MONTHLY', label: 'Monthly' },
          { value: 'AT_CALL', label: 'At Call' },
          { value: 'HOUR_PACKAGE', label: 'Hour Package' },
        ],
      },
      {
        key: 'isActive',
        label: 'Status',
        placeholder: 'All Status',
        type: 'boolean' as const,
        getValue: (item: SupportAgreement) => item.isActive ? 'Active' : 'Inactive',
        options: ['Active', 'Inactive'],
      },
      {
        key: 'startDate',
        label: 'Start Date',
        placeholder: 'All Start Date',
        type: 'date' as const,
        getValue: (item: SupportAgreement) =>
          item.startDate ? formatDateGlobal(item.startDate) : undefined,
        options: [],
      },
      {
        key: 'endDate',
        label: 'End Date',
        placeholder: 'All End Date',
        type: 'date' as const,
        getValue: (item: SupportAgreement) =>
          item.endDate ? formatDateGlobal(item.endDate) : undefined,
        options: [],
      },
    ],
    applyFilter: (
      items: SupportAgreement[],
      filters: {
        description: string[] | string;
        clientId: string[];
        agreementType: string[];
        isActive: string[];
        startDate: string[];
        endDate: string[];
      },
      dateRanges?: Record<string, { start: Date | null; end: Date | null }>
    ) => {
      const ranges = dateRanges || {};
      return items.filter((item) => {
        // Description filter
        const descrFilter = filters.description;
        if (typeof descrFilter === 'string' && descrFilter.trim() !== '') {
          const value = (item.description || '').toLowerCase();
          if (!value.includes(descrFilter.toLowerCase())) return false;
        }

        // Client filter
        if (filters.clientId.length > 0) {
          const clientName = item.client?.clientName || '';
          if (!filters.clientId.includes(clientName)) return false;
        }

        // Agreement type filter
        if (filters.agreementType.length > 0) {
          if (!filters.agreementType.includes(item.agreementType || '')) return false;
        }

        // Status filter
        if (filters.isActive.length > 0) {
          const status = item.isActive ? 'Active' : 'Inactive';
          if (!filters.isActive.includes(status)) return false;
        }

        // Date range filters
        if (ranges.startDate?.start && item.startDate) {
          const itemDate = new Date(item.startDate);
          if (itemDate < ranges.startDate.start) return false;
        }
        if (ranges.startDate?.end && item.startDate) {
          const itemDate = new Date(item.startDate);
          if (itemDate > ranges.startDate.end) return false;
        }
        if (ranges.endDate?.start && item.endDate) {
          const itemDate = new Date(item.endDate);
          if (itemDate < ranges.endDate.start) return false;
        }
        if (ranges.endDate?.end && item.endDate) {
          const itemDate = new Date(item.endDate);
          if (itemDate > ranges.endDate.end) return false;
        }

        return true;
      });
    },
  },

  // Form Configuration
  form: {
    new: {
      title: 'New Support Agreement',
      subtitle: 'Add a new support agreement to your database',
      submitText: 'Create Support Agreement',
    },
    edit: {
      title: 'Edit Support Agreement',
      subtitle: 'Update support agreement information',
      submitText: 'Update Support Agreement',
    },
    cancelText: 'Cancel',
    loadingText: 'Loading support agreement...',
    errorMessage: 'Failed to save support agreement. Please try again.',
    create: async (data: Partial<SupportAgreement>) => {
      const { createSupportAgreement } = await import('../../services/mutations');
      return await createSupportAgreement(data);
    },
    update: async (id: number, data: Partial<SupportAgreement>) => {
      const { updateSupportAgreement } = await import('../../services/mutations');
      return await updateSupportAgreement(id, data);
    },
    delete: async (id: number) => {
      const { deleteSupportAgreement } = await import('../../services/mutations');
      await deleteSupportAgreement(id);
    },
  },

  // Utility functions
  utils: {
    formatDate: (dateString?: string) => {
      if (!dateString) return '-';
      return formatDateGlobal(dateString);
    },
    formatCurrency: (amount?: number) => {
      if (amount === undefined || amount === null) return '-';
      return new Intl.NumberFormat('en-IE', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(amount);
    },
  },

  // Card View Configuration
  cardView: {
    getId: (item: SupportAgreement) => item.id,
    getTitle: (item: SupportAgreement) => item.description || `Agreement #${item.id}`,
    getSubtitle: (item: SupportAgreement) => {
      const typeLabels: Record<SupportAgreementType, string> = {
        'MONTHLY': 'Monthly',
        'AT_CALL': 'At Call',
        'HOUR_PACKAGE': 'Hour Package',
      };
      return typeLabels[item.agreementType] || item.agreementType;
    },
    getStatusBadge: (item: SupportAgreement) => ({
      label: item.isActive ? 'Active' : 'Inactive',
      variant: (item.isActive ? 'success' : 'danger') as 'default' | 'success' | 'warning' | 'danger' | 'info',
    }),
    getCardFields: (item: SupportAgreement) => {
      const fields: Array<{
        label?: string;
        value: React.ReactNode;
        icon?: React.ReactNode;
        badge?: boolean;
        badgeVariant?: 'default' | 'success' | 'warning' | 'danger' | 'info' | 'blue' | 'purple';
      }> = [];

      // Client
      if (item.client?.clientName) {
        fields.push({
          value: item.client.clientName,
          icon: (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          ),
        });
      }

      // Agreement Type
      const typeLabels: Record<SupportAgreementType, string> = {
        'MONTHLY': 'Monthly',
        'AT_CALL': 'At Call',
        'HOUR_PACKAGE': 'Hour Package',
      };
      fields.push({
        value: typeLabels[item.agreementType] || item.agreementType,
        badge: true,
        badgeVariant: 'blue' as const,
      });

      // Dates
      if (item.startDate || item.endDate) {
        const dateStr = [
          item.startDate ? formatDateGlobal(item.startDate) : '',
          item.endDate ? formatDateGlobal(item.endDate) : '',
        ].filter(Boolean).join(' - ');
        fields.push({
          value: dateStr,
          icon: (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          ),
        });
      }

      // Type-specific fields
      if (item.agreementType === 'MONTHLY' && item.monthlyFee !== undefined) {
        fields.push({
          value: `€${item.monthlyFee.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/month`,
          icon: (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          ),
        });
      } else if (item.agreementType === 'AT_CALL' && item.hourlyRate !== undefined) {
        fields.push({
          value: `€${item.hourlyRate.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/hour`,
          icon: (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          ),
        });
      } else if (item.agreementType === 'HOUR_PACKAGE' && item.totalMinutes !== undefined) {
        const remaining = item.totalMinutes - (item.usedMinutes || 0);
        fields.push({
          value: `${item.totalMinutes} min total${remaining >= 0 ? ` (${remaining} remaining)` : ''}`,
          icon: (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          ),
        });
      }

      return fields;
    },
    statusFilter: {
      enabled: true,
      defaultFilter: 'all',
      options: [
        { value: 'all', label: 'All Agreements' },
        { value: 'Active', label: 'Active Only' },
        { value: 'Inactive', label: 'Inactive Only' },
      ],
      getStatus: (item: SupportAgreement) => item.isActive ? 'Active' : 'Inactive',
    },
    updateStatus: async (id: number, newStatus: any) => {
      const { updateSupportAgreement } = await import('../../services/mutations');
      await updateSupportAgreement(id, { isActive: newStatus });
    },
    renderStatusSelector: (item: SupportAgreement, isUpdating: boolean, onStatusChange: (newStatus: any) => void) => {
      return (
        <Select
          value={item.isActive ? 'active' : 'inactive'}
          onChange={(e: any) => {
            onStatusChange(e.target.value === 'active');
          }}
          disabled={isUpdating}
          className="w-full text-xs"
          showClearButton={false}
          options={[
            { value: 'active', label: 'Active' },
            { value: 'inactive', label: 'Inactive' },
          ]}
        />
      );
    },
    actions: {
      view: true,
      edit: true,
    },
  },

  // Detail View Configuration
  detail: {
    subtitle: 'Support Agreement Details',
    notFoundTitle: 'Support Agreement not found',
    notFoundMessage: 'The support agreement you\'re looking for doesn\'t exist.',
    notFoundBackText: 'Back to Support Agreements',
    defaultTitle: 'Unnamed Support Agreement',
    getTitle: (item: SupportAgreement) => {
      return item.description || `Support Agreement #${item.id}`;
    },
  },

  // Toolbar Actions Configuration
  toolbar: {
    actions: [
      {
        key: 'download',
        icon: 'download',
        title: 'Download',
        showTitle: false,
        variant: 'default' as const,
        dropdown: [
          {
            key: 'export-excel',
            label: 'Export to Excel',
            icon: 'download',
            onClick: async (exportData?: {
              sortedData: any[];
              tableColumns: any[];
              entityName: string;
              exportFileName: string;
              formatDate?: (dateString?: string) => string;
            }) => {
              if (!exportData) {
                console.error('Export data not provided');
                return;
              }

              try {
                const { exportToExcel } = await import('../../components/exports');
                await exportToExcel(exportData);
              } catch (error: any) {
                console.error('Failed to export to Excel:', error);
                throw new Error(error?.message || 'Failed to export to Excel. Please check the console for details.');
              }
            },
          },
        ],
      },
      {
        key: 'add',
        icon: 'add',
        title: 'Add New Support Agreement',
        showTitle: false,
        variant: 'primary' as const,
        href: '/support-agreements/new',
      },
    ],
  },

  // Table Configuration
  table: {
    actionsPosition: 'left' as const,
    onRowClick: (item: SupportAgreement) => {
      window.location.href = `/support-agreements/${item.id}`;
    },
    keyExtractor: (item: SupportAgreement) => item.id,
    getClientId: (item: SupportAgreement) => item.clientId ?? null,
  },

  // Empty States
  emptyStates: {
    noData: 'Get started by creating a new support agreement.',
    noSearchResults: 'No support agreements found matching your search criteria.',
    noFilterResults: 'No support agreements match the selected filters.',
  },

  // Delete Confirmation
  delete: {
    title: 'Delete Support Agreement',
    message: 'Are you sure you want to delete this support agreement? This action cannot be undone.',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    errorMessage: 'Failed to delete support agreement. Please try again.',
    handler: async (id: number) => {
      await moduleConfig.form.delete(id);
    },
  },

  // Unified Field Configuration
  fields: [
    {
      name: 'clientId',
      label: 'Client',
      type: 'select',
      placeholder: 'Select a client',
      required: true,
      options: [],
      apiEndpoint: '/crm-clients',
      labelField: 'clientName',
      validation: {
        required: true,
      },
    },
    {
      name: 'agreementType',
      label: 'Agreement Type',
      type: 'select',
      placeholder: 'Select agreement type',
      required: true,
      options: [
        { value: 'MONTHLY', label: 'Monthly' },
        { value: 'AT_CALL', label: 'At Call' },
        { value: 'HOUR_PACKAGE', label: 'Hour Package' },
      ],
      validation: {
        required: true,
      },
    },
    {
      name: 'agreementTypeId',
      label: 'Service Type',
      type: 'select',
      placeholder: 'Select service type (optional)',
      options: [],
      apiEndpoint: '/p-crm-service-type',
      labelField: 'pst_descr',
    },
    {
      name: 'categoryId',
      label: 'Category',
      type: 'select',
      placeholder: 'Select category (optional)',
      options: [],
      apiEndpoint: '/p-crm-service-category',
      labelField: 'psc_descr',
    },
    {
      name: 'description',
      label: 'Description',
      type: 'textarea',
      placeholder: 'Enter description',
      required: true,
      rows: 4,
      validation: {
        required: true,
        maxLength: 1000,
      },
    },
    {
      name: 'startDate',
      label: 'Start Date',
      type: 'date',
      placeholder: 'Select start date',
    },
    {
      name: 'endDate',
      label: 'End Date',
      type: 'date',
      placeholder: 'Select end date',
    },
    {
      name: 'isActive',
      label: 'Active',
      type: 'checkbox',
      placeholder: 'Is this agreement active?',
    },
    {
      name: 'notes',
      label: 'Notes',
      type: 'textarea',
      placeholder: 'Enter notes',
      rows: 5,
    },
    // MONTHLY fields
    {
      name: 'monthlyFee',
      label: 'Monthly Fee',
      type: 'number',
      placeholder: 'Enter monthly fee',
      prefix: <span className="text-gray-500">€</span>,
      step: 0.01,
      conditional: {
        field: 'agreementType',
        value: 'MONTHLY',
      },
    },
    {
      name: 'includedHours',
      label: 'Included Hours',
      type: 'number',
      placeholder: 'Enter included hours per month',
      step: 1,
      conditional: {
        field: 'agreementType',
        value: 'MONTHLY',
      },
    },
    {
      name: 'overageRate',
      label: 'Overage Rate',
      type: 'number',
      placeholder: 'Enter rate per hour for hours over included',
      prefix: <span className="text-gray-500">€</span>,
      step: 0.01,
      conditional: {
        field: 'agreementType',
        value: 'MONTHLY',
      },
    },
    // AT_CALL fields
    {
      name: 'hourlyRate',
      label: 'Hourly Rate',
      type: 'number',
      placeholder: 'Enter hourly rate',
      prefix: <span className="text-gray-500">€</span>,
      step: 0.01,
      conditional: {
        field: 'agreementType',
        value: 'AT_CALL',
      },
    },
    {
      name: 'minimumHours',
      label: 'Minimum Hours',
      type: 'number',
      placeholder: 'Enter minimum billable hours per call',
      step: 1,
      conditional: {
        field: 'agreementType',
        value: 'AT_CALL',
      },
    },
    // HOUR_PACKAGE fields
    {
      name: 'totalMinutes',
      label: 'Total Minutes',
      type: 'number',
      placeholder: 'Enter total prepaid minutes/hours',
      step: 1,
      conditional: {
        field: 'agreementType',
        value: 'HOUR_PACKAGE',
      },
    },
    {
      name: 'usedMinutes',
      label: 'Used Minutes',
      type: 'number',
      placeholder: 'Enter minutes/hours used',
      step: 1,
      conditional: {
        field: 'agreementType',
        value: 'HOUR_PACKAGE',
      },
    },
    {
      name: 'packagePrice',
      label: 'Package Price',
      type: 'number',
      placeholder: 'Enter total price for the package',
      prefix: <span className="text-gray-500">€</span>,
      step: 0.01,
      conditional: {
        field: 'agreementType',
        value: 'HOUR_PACKAGE',
      },
    },
    // Renewal/Termination
    {
      name: 'autoRenew',
      label: 'Auto Renew',
      type: 'checkbox',
      placeholder: 'Automatically renew this agreement?',
    },
    {
      name: 'terminationDate',
      label: 'Termination Date',
      type: 'date',
      placeholder: 'Select termination date',
    },
    {
      name: 'terminationReason',
      label: 'Termination Reason',
      type: 'textarea',
      placeholder: 'Enter termination reason',
      rows: 3,
    },
    // Payment tracking
    {
      name: 'totalDue',
      label: 'Total Due',
      type: 'number',
      placeholder: 'Enter total amount due',
      prefix: <span className="text-gray-500">€</span>,
      step: 0.01,
    },
  ] as FieldConfig[],

  // Form Layout Configuration (crm-tasks style: gridCols + fieldSpans)
  formLayout: {
    mode: 'default',
    sections: [
      {
        id: 'basic',
        title: 'Basic Information',
        icon: '📋',
        gridCols: 'md:grid-cols-12',
        fields: ['clientId', 'agreementType', 'agreementTypeId', 'categoryId', 'description', 'isActive'],
        fieldSpans: {
          clientId: 'md:col-span-6',
          agreementType: 'md:col-span-6',
          agreementTypeId: 'md:col-span-6',
          categoryId: 'md:col-span-6',
          description: 'md:col-span-12',
          isActive: 'md:col-span-6',
        },
        collapsible: true,
      },
      {
        id: 'dates',
        title: 'Date Information',
        icon: '📅',
        gridCols: 'md:grid-cols-2',
        fields: ['startDate', 'endDate'],
        collapsible: true,
      },
      {
        id: 'monthly',
        title: 'Monthly Agreement Details',
        icon: '💰',
        gridCols: 'md:grid-cols-2',
        fields: ['monthlyFee', 'includedHours', 'overageRate'],
        collapsible: true,
      },
      {
        id: 'atCall',
        title: 'At Call Agreement Details',
        icon: '📞',
        gridCols: 'md:grid-cols-2',
        fields: ['hourlyRate', 'minimumHours'],
        collapsible: true,
      },
      {
        id: 'hourPackage',
        title: 'Hour Package Details',
        icon: '📦',
        gridCols: 'md:grid-cols-2',
        fields: ['totalMinutes', 'usedMinutes', 'packagePrice'],
        collapsible: true,
      },
      {
        id: 'renewal',
        title: 'Renewal & Termination',
        icon: '🔄',
        gridCols: 'md:grid-cols-12',
        fields: ['autoRenew', 'terminationDate', 'terminationReason'],
        fieldSpans: {
          autoRenew: 'md:col-span-6',
          terminationDate: 'md:col-span-6',
          terminationReason: 'md:col-span-12',
        },
        collapsible: true,
      },
      {
        id: 'payment',
        title: 'Payment Tracking',
        icon: '💳',
        gridCols: 'md:grid-cols-2',
        fields: ['totalDue'],
        collapsible: true,
      },
      {
        id: 'notes',
        title: 'Additional Notes',
        icon: '📝',
        gridCols: 'md:grid-cols-12',
        fields: ['notes'],
        fieldSpans: { notes: 'md:col-span-12' },
        collapsible: true,
      },
    ],
  } as FormLayout,
};

export type SupportAgreementsListContext = {
  onOpenRenewalRemindersPanel?: (item: SupportAgreement) => void;
};

// Helper function to get columns
export const getColumns = (
  formatDate: (dateString?: string) => string,
  formatCurrency: (amount?: number) => string,
  colors?: { textMuted?: string },
  listContext?: SupportAgreementsListContext
): Column<SupportAgreement>[] => [
  {
    key: 'description',
    header: 'Description',
    sortable: true,
    render: (value, row) => {
      const clientLine = row.client?.clientName || (row.clientId ? `Client #${row.clientId}` : null);
      if (!clientLine) return value || '-';
      return (
        <div className="flex flex-col min-w-0">
          <span className="truncate">{value || '-'}</span>
          <span className="text-sm text-gray-500 dark:text-gray-400 truncate mt-0.5">{clientLine}</span>
        </div>
      );
    },
  },
  {
    key: 'agreementType',
    header: 'Type',
    sortable: true,
    render: (value) => {
      if (!value) return <span className="text-sm text-gray-500">-</span>;
      const typeLabels: Record<SupportAgreementType, string> = {
        MONTHLY: 'Monthly',
        AT_CALL: 'At Call',
        HOUR_PACKAGE: 'Hour Package',
      };
      return typeLabels[value as SupportAgreementType] || value;
    },
  },
  {
    key: 'isActive',
    header: 'Status',
    sortable: true,
    render: (_value, row) => renderSupportAgreementStatusBadge(row.isActive),
  },
  {
    key: 'startDate',
    header: 'Start Date',
    sortable: true,
    render: (value) => formatDate(value),
  },
  {
    key: 'endDate',
    header: 'End Date',
    sortable: true,
    render: (value) => formatDate(value),
  },
  {
    key: 'monthlyFee',
    header: 'Monthly Fee',
    sortable: true,
    render: (value, row) => {
      if (row.agreementType === 'MONTHLY' && value) {
        return formatCurrency(value as number);
      }
      return <span className="text-sm text-gray-500">-</span>;
    },
  },
  {
    key: 'hourlyRate',
    header: 'Hourly Rate',
    sortable: true,
    render: (value, row) => {
      if (row.agreementType === 'AT_CALL' && value) {
        return formatCurrency(value as number);
      }
      return <span className="text-sm text-gray-500">-</span>;
    },
  },
  {
    key: 'packagePrice',
    header: 'Package Price',
    sortable: true,
    render: (value, row) => {
      if (row.agreementType === 'HOUR_PACKAGE' && value) {
        return formatCurrency(value as number);
      }
      return <span className="text-sm text-gray-500">-</span>;
    },
  },
  {
    key: 'renewalReminders',
    header: 'Renewal Reminders',
    sortable: false,
    render: (_value, item) => {
      const reminders = item.renewalReminders ?? [];
      const count = reminders.length;
      const last = reminders[0];
      const lastDate = last?.contactedAt ? formatDate(last.contactedAt) : null;
      const lastStatusValue = last?.responseStatus ?? null;
      const responseDate = last?.responseAt ? formatDate(last.responseAt) : null;
      const onOpenPanel = listContext?.onOpenRenewalRemindersPanel;
      const textMuted = colors?.textMuted ?? 'text-gray-500 dark:text-gray-400';
      if (count === 0) {
        if (onOpenPanel) {
          return (
            <button type="button" onClick={(e) => { e.stopPropagation(); e.preventDefault(); onOpenPanel(item); }} className="text-indigo-600 hover:text-indigo-800 hover:underline text-left">
              0 reminders
            </button>
          );
        }
        return '-';
      }
      const responseLine = lastStatusValue ? (
        <div className="flex flex-wrap items-center gap-1.5">
          {renderAgreementRenewalStatusBadge(lastStatusValue)}
          {responseDate && <span className={cn('text-xs', textMuted)}>{responseDate}</span>}
        </div>
      ) : (
        <div className={cn('text-xs', textMuted)}>—</div>
      );
      const contactedLine = lastDate && (
        <div className={cn('text-xs mt-0.5', textMuted)}>Contacted {lastDate} · {count} reminder{count !== 1 ? 's' : ''}</div>
      );
      if (onOpenPanel) {
        return (
          <div className="text-left">
            <button type="button" onClick={(e) => { e.stopPropagation(); e.preventDefault(); onOpenPanel(item); }} className="text-left w-full hover:bg-gray-50 dark:hover:bg-gray-800/50 rounded px-1 -mx-1 py-0.5">
              {responseLine}
              {contactedLine}
            </button>
          </div>
        );
      }
      return (
        <div className="text-left">
          {responseLine}
          {contactedLine}
        </div>
      );
    },
  },
];

// Helper function to get detail sections
export const getDetailSections = (
  item: SupportAgreement,
  formatDate: (dateString?: string) => string,
  formatCurrency: (amount?: number) => string
) => {
  const sections: Array<{
    title: string;
    icon: string;
    fields: Array<{ label: string; value: any }>;
  }> = [
    {
      title: 'Basic Information',
      icon: '📋',
      fields: [
        {
          label: 'Description',
          value: item.description || '-',
        },
        {
          label: 'Client',
          value: item.client?.clientName || (item.clientId ? `Client #${item.clientId}` : 'None'),
        },
        {
          label: 'Agreement Type',
          value: item.agreementType === 'MONTHLY' ? 'Monthly' : item.agreementType === 'AT_CALL' ? 'At Call' : item.agreementType === 'HOUR_PACKAGE' ? 'Hour Package' : item.agreementType || '-',
        },
        {
          label: 'Service Type',
          value: renderAgreementServiceTypeBadge(item.agreementTypeDescr?.pst_descr, item.agreementTypeDescr?.pst_color),
        },
        {
          label: 'Category',
          value: item.categoryDescr?.psc_descr || '-',
        },
        {
          label: 'Status',
          value: renderSupportAgreementStatusBadge(item.isActive),
        },
      ],
    },
    {
      title: 'Date Information',
      icon: '📅',
      fields: [
        {
          label: 'Start Date',
          value: formatDate(item.startDate),
        },
        {
          label: 'End Date',
          value: formatDate(item.endDate),
        },
        {
          label: 'Termination Date',
          value: formatDate(item.terminationDate),
        },
      ],
    },
  ];

  // Add type-specific sections
  if (item.agreementType === 'MONTHLY') {
    sections.push({
      title: 'Monthly Agreement Details',
      icon: '💰',
      fields: [
        {
          label: 'Monthly Fee',
          value: formatCurrency(item.monthlyFee),
        },
        {
          label: 'Included Hours',
          value: item.includedHours ? `${item.includedHours} hours` : '-',
        },
        {
          label: 'Overage Rate',
          value: formatCurrency(item.overageRate),
        },
      ],
    });
  } else if (item.agreementType === 'AT_CALL') {
    sections.push({
      title: 'At Call Agreement Details',
      icon: '📞',
      fields: [
        {
          label: 'Hourly Rate',
          value: formatCurrency(item.hourlyRate),
        },
        {
          label: 'Minimum Hours',
          value: item.minimumHours ? `${item.minimumHours} hours` : '-',
        },
      ],
    });
  } else if (item.agreementType === 'HOUR_PACKAGE') {
    sections.push({
      title: 'Hour Package Details',
      icon: '📦',
      fields: [
        {
          label: 'Total Minutes',
          value: item.totalMinutes ? `${item.totalMinutes} minutes` : '-',
        },
        {
          label: 'Used Minutes',
          value: item.usedMinutes ? `${item.usedMinutes} minutes` : '-',
        },
        {
          label: 'Remaining Minutes',
          value: item.totalMinutes && item.usedMinutes ? `${(item.totalMinutes - item.usedMinutes)} minutes` : '-',
        },
        {
          label: 'Package Price',
          value: formatCurrency(item.packagePrice),
        },
      ],
    });
  }

  // Add usage information if available
  if (item.totalMinutesUsed !== undefined) {
    sections.push({
      title: 'Usage Information',
      icon: '📊',
      fields: [
        {
          label: 'Total Minutes Used',
          value: item.totalMinutesUsed ? `${item.totalMinutesUsed} minutes` : '0 minutes',
        },
        {
          label: 'Total Hours Used',
          value: item.totalMinutesUsed ? `${(item.totalMinutesUsed / 60).toFixed(2)} hours` : '0 hours',
        },
      ],
    });
  }

  // Add payment information
  sections.push({
    title: 'Payment Information',
    icon: '💳',
    fields: [
      {
        label: 'Total Paid',
        value: formatCurrency(item.totalPaid),
      },
      {
        label: 'Total Due',
        value: formatCurrency(item.totalDue),
      },
    ],
  });

  // Add renewal/termination
  sections.push({
    title: 'Renewal & Termination',
    icon: '🔄',
    fields: [
      {
        label: 'Auto Renew',
        value: item.autoRenew ? 'Yes' : 'No',
      },
      {
        label: 'Termination Reason',
        value: item.terminationReason || '-',
      },
    ],
  });

  // Renewal reminders (inline section)
  sections.push({
    title: 'Renewal Reminders',
    icon: '🔔',
    fields: [
      {
        label: '',
        value: React.createElement(SupportAgreementRenewalRemindersSection, { supportAgreementId: item.id, variant: 'panel' }),
      },
    ],
  });

  // Add notes
  sections.push({
    title: 'Additional Notes',
    icon: '📝',
    fields: [
      {
        label: 'Notes',
        value: item.notes || '-',
      },
    ],
  });

  return sections;
};

// Helper function to get status badges
export const getStatusBadges = (item: SupportAgreement) => {
  return (
    <>
      {renderSupportAgreementStatusBadge(item.isActive, 'px-3 py-1 text-sm font-semibold')}
      {item.agreementType && (
        <Badge variant="info">
          {item.agreementType === 'MONTHLY' ? 'Monthly' : item.agreementType === 'AT_CALL' ? 'At Call' : 'Hour Package'}
        </Badge>
      )}
    </>
  );
};

// Helper function to render toolbar icon
export const getToolbarIcon = (iconName: string): React.ReactNode => {
  switch (iconName) {
    case 'download':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
        </svg>
      );
    case 'add':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
      );
    case 'cardboard':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
        </svg>
      );
    default:
      return null;
  }
};

// Helper function to render table action buttons
export const getTableActions = (
  item: SupportAgreement,
  onDelete: (id: number) => void,
  _colors?: unknown,
  _listContext?: unknown,
  options?: { canDelete?: boolean; canEdit?: boolean }
): React.ReactNode => {
  const canDelete = options?.canDelete !== false;
  const canEdit = options?.canEdit !== false;
  return (
    <StandardTableActions
      viewTo={moduleConfig.module.route.detail(item.id)}
      editTo={moduleConfig.module.route.edit(item.id)}
      onDelete={() => onDelete(item.id)}
      canEdit={canEdit}
      canDelete={canDelete}
    />
  );
};


export const getInitialFormData = (): Partial<SupportAgreement> => {
  return {
    clientId: undefined,
    agreementType: undefined,
    agreementTypeId: undefined,
    categoryId: undefined,
    description: '',
    startDate: undefined,
    endDate: undefined,
    isActive: true,
    notes: '',
    monthlyFee: undefined,
    includedHours: undefined,
    overageRate: undefined,
    hourlyRate: undefined,
    minimumHours: undefined,
    totalMinutes: undefined,
    usedMinutes: 0,
    packagePrice: undefined,
    autoRenew: false,
    terminationDate: undefined,
    terminationReason: '',
    totalDue: undefined,
  };
};

// Helper function to map item to form data
export const mapItemToFormData = (item: SupportAgreement): Partial<SupportAgreement> => {
  return {
    clientId: item.clientId,
    agreementType: item.agreementType,
    agreementTypeId: item.agreementTypeId,
    categoryId: item.categoryId,
    description: item.description,
    startDate: item.startDate,
    endDate: item.endDate,
    isActive: item.isActive,
    notes: item.notes,
    monthlyFee: item.monthlyFee,
    includedHours: item.includedHours,
    overageRate: item.overageRate,
    hourlyRate: item.hourlyRate,
    minimumHours: item.minimumHours,
    totalMinutes: item.totalMinutes,
    usedMinutes: item.usedMinutes,
    packagePrice: item.packagePrice,
    autoRenew: item.autoRenew,
    terminationDate: item.terminationDate,
    terminationReason: item.terminationReason,
    totalDue: item.totalDue,
  };
};

export type ModuleConfig = typeof moduleConfig;
